import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * Operational Status Component
 * Shows real-time sync status, last update timestamp, and data freshness indicators
 */
export default function OperationalStatus({
  lastUpdated = null,
  isLoading = false,
  isError = false,
  onRefresh = null,
  autoRefreshInterval = 0,
}) {
  const [timeSinceUpdate, setTimeSinceUpdate] = useState('');
  const [isStale, setIsStale] = useState(false);

  // Update time since last update every minute
  useEffect(() => {
    if (!lastUpdated) return;

    const updateTimeSince = () => {
      const now = new Date();
      const diff = Math.floor((now - lastUpdated) / 1000);

      if (diff < 60) {
        setTimeSinceUpdate('À l\'instant');
        setIsStale(false);
      } else if (diff < 3600) {
        const minutes = Math.floor(diff / 60);
        setTimeSinceUpdate(`Il y a ${minutes} min`);
        setIsStale(diff > 300); // Stale after 5 minutes
      } else {
        const hours = Math.floor(diff / 3600);
        setTimeSinceUpdate(`Il y a ${hours}h`);
        setIsStale(true);
      }
    };

    updateTimeSince();
    const interval = setInterval(updateTimeSince, 60000);
    return () => clearInterval(interval);
  }, [lastUpdated]);

  const getStatusIndicator = () => {
    if (isLoading) {
      return {
        color: 'bg-blue-500',
        pulse: true,
        text: 'Synchronisation...',
        textColor: 'text-blue-600',
      };
    }
    if (isError) {
      return {
        color: 'bg-red-500',
        pulse: false,
        text: 'Erreur de connexion',
        textColor: 'text-red-600',
      };
    }
    if (isStale) {
      return {
        color: 'bg-amber-500',
        pulse: false,
        text: 'Données obsolètes',
        textColor: 'text-amber-600',
      };
    }
    return {
      color: 'bg-green-500',
      pulse: true,
      text: 'En temps réel',
      textColor: 'text-green-600',
    };
  };

  const status = getStatusIndicator();

  return (
    <div className="flex items-center justify-between px-4 sm:px-6 lg:px-9 py-3 bg-gray-50 border-b border-gray-200">
      <div className="flex items-center gap-3">
        {/* Status indicator */}
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            {status.pulse && (
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${status.color} opacity-75`}></span>
            )}
            <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${status.color}`}></span>
          </span>
          <span className={`text-xs font-medium ${status.textColor}`}>
            {status.text}
          </span>
        </div>

        {/* Last updated timestamp */}
        {lastUpdated && !isLoading && (
          <span className="text-xs text-gray-500 hidden md:inline">
            • Mis à jour {timeSinceUpdate.toLowerCase()}
          </span>
        )}
      </div>

      {/* Refresh button */}
      {onRefresh && (
        <button
          onClick={onRefresh}
          disabled={isLoading}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
            />
          </svg>
          <span className="hidden md:inline">Rafraîchir</span>
        </button>
      )}
    </div>
  );
}

OperationalStatus.propTypes = {
  lastUpdated: PropTypes.instanceOf(Date),
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  onRefresh: PropTypes.func,
  autoRefreshInterval: PropTypes.number,
};
